// Helper function to load logo and generate PDF matching Data Sheet format
import { getMediaUrl } from '../../utils/imageUtils';

export const generateDataSheetPDF = async (surveyData, imageToBase64) => {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;
  let yPosition = margin + 3;
  const cellHeight = 7;

  const FONT_SIZE = 9;
  const FULL_WIDTH = 190;

  // Section Heading (bordered, no gap)
  const addSectionHeading = (text) => {
    const headingTable = [[text]];
    yPosition = addTable(headingTable, margin, yPosition, [FULL_WIDTH]);
  };

  const LOGO_HEIGHT = 18;

  const checkNewPage = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > pageHeight - margin - 15) {
      doc.addPage();
      yPosition = LOGO_HEIGHT + margin;
      return true;
    }
    return false;
  };

  const addTable = (data, x, y, colWidths, boldFirstCol = false) => {
    let currentY = y;
    doc.setFontSize(FONT_SIZE);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.1);
    if (!data || data.length === 0) return currentY;

    const rowHeights = data.map((row) => {
      if (!row || !Array.isArray(row)) return cellHeight;
      let maxLines = 1;
      row.forEach((cell, colIndex) => {
        const width = colWidths[colIndex];
        if (width && width > 0) {
          const text = String(cell || '').trim();
          if (text) maxLines = Math.max(maxLines, doc.splitTextToSize(text, width - 4).length);
        }
      });
      return Math.max(cellHeight, maxLines * 5 + 4);
    });

    data.forEach((row, rowIndex) => {
      if (!row || !Array.isArray(row)) return;
      let currentX = x;
      const rowHeight = rowHeights[rowIndex] || cellHeight;
      if (currentY + rowHeight > pageHeight - margin - 15) {
        doc.addPage();
        currentY = LOGO_HEIGHT + margin;
        currentX = x;
      }
      row.forEach((cell, colIndex) => {
        const width = colWidths[colIndex];
        if (!width || width <= 0 || isNaN(width)) return;
        try { doc.rect(currentX, currentY, width, rowHeight); } catch (e) { }
        const isBold = rowIndex === 0 || (boldFirstCol && colIndex === 0);
        doc.setFont(undefined, isBold ? 'bold' : 'normal');
        doc.setFontSize(FONT_SIZE);
        const text = String(cell || '').trim();
        if (text && width > 4) {
          const textLines = doc.splitTextToSize(text, width - 4);
          textLines.forEach((line, lineIndex) => {
            const lineY = currentY + 5 + (lineIndex * 5);
            if (lineY < currentY + rowHeight - 2) doc.text(line, currentX + 2, lineY, { maxWidth: width - 4 });
          });
        }
        currentX += width;
      });
      currentY += rowHeight;
    });
    return currentY;
  };

  // ── Fetch logo first, then document images one by one ────────────────────
  const logoPath = '/assets/icon/valfin_logo.png';
  const logoUrl = logoPath.startsWith('http') ? logoPath : window.location.origin + logoPath;
  const logoBase64 = await imageToBase64(logoUrl).catch(() => null);

  // ── Image optimisation helper ─────────────────────────────────────────────
  // Strategy: canvas-resize to max 1200px wide + convert to JPEG at 85% quality.
  // A jsPDF page is ~540px wide at 72dpi, so anything above 1200px is invisible
  // extra data. This typically reduces a 3–5 MB phone photo to ~150–300 KB
  // with no visible quality loss at normal PDF viewing / print size.
  //
  // TO REVERT to original quality: replace the entire optimiseImage function
  // body with just:  return base64;
  // That will pass the raw base64 straight through without any resizing.
  const optimiseImage = (base64) => new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        // Max width for PDF images — increase if photos look blurry after revert
        const MAX_WIDTH = 1200;
        const scale = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        // JPEG quality 0.85 — range 0.0 (worst) to 1.0 (lossless).
        // Increase to 0.92 if images look noticeably degraded.
        const optimised = canvas.toDataURL('image/jpeg', 0.85);
        resolve(optimised);
      };
      img.onerror = () => resolve(base64); // fallback: use original on error
      img.src = base64;
    } catch {
      resolve(base64); // fallback: use original if canvas is unavailable
    }
  });

  const docItems = (surveyData.documents || []).filter(d => d.file_path);
  const docImageMap = new Map();
  for (const docItem of docItems) {
    const url = getMediaUrl(docItem.file_path);
    const raw = await imageToBase64(url).catch(() => null);
    if (!raw) continue;
    // Optimise before storing — keeps only one image in memory at a time
    const optimised = await optimiseImage(raw);
    docImageMap.set(docItem.file_path, optimised);
  }
  // ─────────────────────────────────────────────────────────────────────────

  // Add logo on first page
  if (logoBase64) {
    doc.addImage(logoBase64, 'PNG', pageWidth - 30, margin, 20, 8);
  }

  // Title - SITE VISIT DATA SHEET
  yPosition = margin + 8; // Space for logo
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 51, 102); // Dark blue color
  doc.text('SITE VISIT DATA SHEET', pageWidth / 2, yPosition, { align: 'center' });
  doc.setTextColor(0, 0, 0); // Reset to black
  yPosition += 12;

  // Date and Property Type row
  const surveyDate = surveyData.survey_date
    ? new Date(surveyData.survey_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';
  const propertyType = surveyData.survey_details?.property_type || '';

  checkNewPage(12);
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.text('DATE: ', margin, yPosition);
  doc.setFont(undefined, 'normal');
  doc.text(surveyDate, margin + doc.getTextWidth('DATE: '), yPosition);
  if (propertyType) {
    doc.setFont(undefined, 'bold');
    doc.text('TYPE OF PROPERTY: ', margin + 70, yPosition);
    doc.setFont(undefined, 'normal');
    doc.text(propertyType.toUpperCase(), margin + 70 + doc.getTextWidth('TYPE OF PROPERTY: '), yPosition);
  }
  yPosition += 8;

  // Bank Name and Customer Name table
  checkNewPage(18);
  const propertyDetails = surveyData.property_details || {};
  const bankCustomerTable = [
    ['BANK NAME', 'CUSTOMER NAME'],
    [propertyDetails.client || '', propertyDetails.customer_name || '']
  ];
  yPosition = addTable(bankCustomerTable, margin, yPosition, [95, 95]);
  yPosition += 4;

  // Contact Person and Address
  checkNewPage(18);
  const contactTable = [
    ['CONTACT PERSON NAME', 'Phone Number'],
    [propertyDetails.contact_person_name || '', propertyDetails.contact_person_phone_num || '']
  ];
  yPosition = addTable(contactTable, margin, yPosition, [95, 95]);
  yPosition += 4;

  checkNewPage(18);
  const addressTable = [
    ['ACTUAL ADDRESS'],
    [surveyData.survey_details?.actual_address || propertyDetails.property_address || '']
  ];
  yPosition = addTable(addressTable, margin, yPosition, [190]);
  yPosition += 6;

  // Address Matching, Numbered, Road Type, Facilities table
  const roadInfo = surveyData.survey_details?.road_info || {};
  const facilities = surveyData.survey_details?.facilities || {};
  const cb = (val) => val ? '[+]' : '[ ]';

  // Draw a visual checkbox at position (x, y): box + tick if checked
  const drawCheckbox = (x, y, checked, size = 3) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(x, y, size, size);
    if (checked) {
      doc.setLineWidth(0.4);
      doc.lines(
        [[size * 0.3, size * 0.3], [size * 0.4, -size * 0.4]],
        x + size * 0.15,
        y + size * 0.4
      );
    }
    doc.setLineWidth(0.1);
  };

  // Draw a row of labeled checkboxes, returns the x end position
  const drawCheckboxRow = (items, startX, y) => {
    let x = startX;
    const boxSize = 3;
    const gap = 2;
    doc.setFontSize(FONT_SIZE);
    doc.setFont(undefined, 'normal'); // always normal for checkbox labels
    items.forEach(({ label, checked }) => {
      drawCheckbox(x, y - boxSize + 0.5, checked, boxSize);
      x += boxSize + 1;
      doc.setFont(undefined, 'normal');
      doc.text(label, x, y);
      x += doc.getTextWidth(label) + gap;
    });
  };
  const addressMatching = surveyData.survey_details?.address_matching;
  const addressMatchingValue = addressMatching === true ? 'YES' : addressMatching === false ? 'NO' : '';

  const utilityBills = surveyData.survey_details?.utility_bills || {};

  const numberedValue = surveyData.survey_details?.numbered === true ? 'YES' : surveyData.survey_details?.numbered === false ? 'NO' : '';

  const demarcatedValue = surveyData.survey_details?.demarcated === true ? 'YES' : surveyData.survey_details?.demarcated === false ? 'NO' : '';

  const infoTable = [
    ['ADDRESS MATCHING', addressMatchingValue, `DEMARCATE: ${demarcatedValue}`],
    ['NUMBERED', numberedValue, ''],
    ['TYPE OF ROAD', roadInfo.type?.toUpperCase() || '', `WIDTH: ${roadInfo.width_ft || ''} FT`],
    ['LAT & LONG', `${surveyData.latitude || ''}, ${surveyData.longitude || ''}`, ''],
  ];
  yPosition = addTable(infoTable, margin, yPosition, [60, 65, 65], false);
  yPosition += 3;

  // Utility Bills row with real checkboxes inside a bordered row
  checkNewPage(10);
  const utilRowHeight = cellHeight;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.1);
  doc.rect(margin, yPosition, 190, utilRowHeight);
  doc.setFontSize(FONT_SIZE);
  doc.setFont(undefined, 'bold');
  doc.text('UTILITY BILLS:', margin + 2, yPosition + 4);
  drawCheckboxRow([
    { label: 'EC', checked: utilityBills.ec },
    { label: 'Gas', checked: utilityBills.gas },
    { label: 'Water', checked: utilityBills.water },
    { label: 'ID Cards', checked: utilityBills.id_cards },
  ], margin + 32, yPosition + 4);
  yPosition += utilRowHeight;

  // Facilities row with real checkboxes inside a bordered row
  checkNewPage(10);
  doc.rect(margin, yPosition, 190, utilRowHeight);
  doc.setFont(undefined, 'bold');
  doc.text('FACILITIES:', margin + 2, yPosition + 4);
  drawCheckboxRow([
    { label: 'UGD', checked: facilities.ugd },
    { label: 'Drains', checked: facilities.drains },
    { label: 'Electricity', checked: facilities.electricity },
    { label: 'Water Supply', checked: facilities.water_supply },
  ], margin + 24, yPosition + 4);
  yPosition += utilRowHeight;
  yPosition += 6;

  // Actual Schedule (Boundaries)
  checkNewPage(30);
  const boundaries = surveyData.survey_details?.boundaries || {};

  addSectionHeading('ACTUAL SCHEDULE');

  const scheduleTable = [
    ['EAST', 'WEST', 'NORTH', 'SOUTH'],
    [boundaries.east || '', boundaries.west || '', boundaries.north || '', boundaries.south || '']
  ];
  yPosition = addTable(scheduleTable, margin, yPosition, [47.5, 47.5, 47.5, 47.5]);
  yPosition += 6;

  // Amenities
  checkNewPage(14);
  const amenities = surveyData.survey_details?.amenities || '';
  const amenitiesTable = [['AMENITIES', amenities.toUpperCase() || '']];
  yPosition = addTable(amenitiesTable, margin, yPosition, [50, 140]);
  yPosition += 4;

  // Plot Area table
  checkNewPage(25);
  const measurements = surveyData.survey_details?.measurements || {};

  addSectionHeading('DIMENSIONS');

  const plotTable = [
    ['PLOT AREA/UDS', 'EAST', 'WEST', 'NORTH', 'SOUTH'],
    [
      measurements.plot_area_uds_sft || '',
      measurements.east_ft || '',
      measurements.west_ft || '',
      measurements.north_ft || '',
      measurements.south_ft || ''
    ]
  ];
  yPosition = addTable(plotTable, margin, yPosition, [38, 38, 38, 38, 38]);
  yPosition += 6;

  // Actual Setbacks table
  checkNewPage(30);
  const setbacks = surveyData.survey_details?.setbacks || {};

  addSectionHeading('ACTUAL SETBACK');

  const actualSetbackTable = [
    ['EAST', 'WEST', 'NORTH', 'SOUTH'],
    [`${setbacks.east_ft ?? ''} FT`, `${setbacks.west_ft ?? ''} FT`, `${setbacks.north_ft ?? ''} FT`, `${setbacks.south_ft ?? ''} FT`]
  ];
  yPosition = addTable(actualSetbackTable, margin, yPosition, [47.5, 47.5, 47.5, 47.5]);
  yPosition += 6;

  // Structure Type and Building Config
  checkNewPage(18);
  // const structure = surveyData.survey_details?.structure?.type || '';
  const buildingConfig = surveyData.survey_details?.building_config || {};


  // Buildings with floors and flooring per floor
  const buildings = buildingConfig.buildings || [];

  if (buildings.length > 0) {
    buildings.forEach((building, index) => {
      const structure = building.structure_type || '';
      const floors = building.floors || [];

      // const requiredSpace = (floors.length + 2) * cellHeight + 6;
      const requiredSpace = (floors.length + 3) * cellHeight + 10;
      checkNewPage(requiredSpace);

      addSectionHeading(`BUILDING DETAILS : Building ${index + 1} - ${building.name || ''}`);

      const buildingTableData = [
        ['FLOOR', 'FLOORING', `STRUCTURE: ${structure.toUpperCase()}`],
        ...floors.map(floor => [floor.name || '', floor.flooring || '', ''])
      ];
      yPosition = addTable(buildingTableData, margin, yPosition, [70, 60, 60]);
      yPosition += 6;
    });
  }
  yPosition += 2;


  // Floor Details Table

  if (surveyData.floor_details && surveyData.floor_details.length > 0) {

    const groupedFloors = {};

    surveyData.floor_details.forEach(floor => {
      const building = floor.building_label || 'UNKNOWN';

      if (!groupedFloors[building]) {
        groupedFloors[building] = [];
      }

      groupedFloors[building].push(floor);
    });

    Object.keys(groupedFloors).forEach((buildingName, index) => {
      const floors = groupedFloors[buildingName];

      checkNewPage((floors.length + 4) * cellHeight + 20);

      addSectionHeading(`FLOOR DETAILS : Building ${index + 1} - ${buildingName.split(' - ')[1] || buildingName}`);

      //  Table
      const floorHeaders = [
        'FLOORS',
        'BUA/SBUA',
        'STAIR CASE, BALCONY & PORTICO AREA',
        'UNITS',
        'USAGE',
        'AGE IN YEARS',
        'OCCUPIED BY - PERIOD',
        'RENT/LEASE DETAILS'
      ];

      const floorRows = floors.map(floor => [
        floor.floor_label || floor.floor_type?.toUpperCase() || '',
        floor.bua_sbua || '',
        floor.stair_balcony_portico_area || '',
        floor.units || '',
        floor.usage || '',
        floor.age_in_years || '',
        floor.occupied_by_period_years || '',
        floor.rent_amount != null ?
          `Rent - Rs.${Number(floor.rent_amount).toLocaleString()}`
          : floor.lease_amount != null
            ? `Lease - Rs.${Number(floor.lease_amount).toLocaleString()}`
            : 'N/A'
      ]);

      const floorTable = [floorHeaders, ...floorRows];

      yPosition = addTable(
        floorTable,
        margin,
        yPosition,
        [16, 16, 40, 16, 16, 14, 32, 40]
      );

      yPosition += 6;
    });
  }

  // Surrounding Features Table
  checkNewPage(60);
  const surroundingDetails = surveyData.surrounding_details || {};
  const surroundingFeatures = surroundingDetails.features || [];

  const getFeature = (typeKey) => surroundingFeatures.find(f => f.type === typeKey) || {};

  const colWidths = [60, 30, 100];

  const buildFeatureRow = (label, feature, extraFields = []) => {
    const exists = feature.exists ? 'YES' : 'NO';
    const segments = feature.exists ? extraFields.map(({ key, label: l }) => {
      const val = feature[key];
      return val !== undefined && val !== null ? { label: l, value: String(val) } : null;
    }).filter(Boolean) : [];
    return { label, exists, segments };
  };

  const drain = getFeature('rajakalve_drain');
  const lake = getFeature('lake_pond');
  const graveyard = getFeature('graveyard');
  const htPole = getFeature('ht_pole');
  const railway = getFeature('railway_line');

  const featureRows = [
    buildFeatureRow('RAJAKALUVE/DRAIN', drain, [{ key: 'coverage', label: 'Coverage' }, { key: 'width_ft', label: 'Width(ft)' }, { key: 'distance_ft', label: 'Distance(ft)' }]),
    buildFeatureRow('LAKE/POND', lake, [{ key: 'distance_ft', label: 'Distance(ft)' }]),
    buildFeatureRow('GRAVEYARD', graveyard, [{ key: 'distance_ft', label: 'Distance(ft)' }, { key: 'other', label: 'Other' }]),
    buildFeatureRow('HT.LANE/HT.POLE', htPole, [{ key: 'distance_ft', label: 'Distance(ft)' }]),
    buildFeatureRow('RAILWAY LINE', railway, [{ key: 'distance_ft', label: 'Distance(ft)' }]),
  ];

  // Draw header row
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.1);
  let cx = margin;
  ['FEATURE', 'EXISTS', 'DETAILS'].forEach((h, i) => {
    doc.rect(cx, yPosition, colWidths[i], cellHeight);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(FONT_SIZE);
    doc.text(h, cx + 2, yPosition + 4);
    cx += colWidths[i];
  });
  yPosition += cellHeight;

  // Draw feature rows
  featureRows.forEach(({ label, exists, segments }) => {
    if (yPosition + cellHeight > pageHeight - margin - 15) {
      doc.addPage();
      yPosition = LOGO_HEIGHT + margin;
    }
    cx = margin;
    // Col 0: feature name bold
    doc.rect(cx, yPosition, colWidths[0], cellHeight);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(FONT_SIZE);
    doc.text(label, cx + 2, yPosition + 4, { maxWidth: colWidths[0] - 4 });
    cx += colWidths[0];
    // Col 1: exists normal
    doc.rect(cx, yPosition, colWidths[1], cellHeight);
    doc.setFont(undefined, 'normal');
    doc.text(exists, cx + 2, yPosition + 4);
    cx += colWidths[1];
    // Col 2: segments with bold label + normal value
    doc.rect(cx, yPosition, colWidths[2], cellHeight);
    let segX = cx + 2;
    segments.forEach((seg, i) => {
      if (i > 0) segX += 3;
      doc.setFont(undefined, 'bold');
      const lbl = seg.label + ': ';
      doc.text(lbl, segX, yPosition + 4);
      segX += doc.getTextWidth(lbl);
      doc.setFont(undefined, 'normal');
      doc.text(seg.value, segX, yPosition + 4);
      segX += doc.getTextWidth(seg.value);
    });
    yPosition += cellHeight;
  });

  // OS Number row
  cx = margin;
  doc.rect(cx, yPosition, colWidths[0], cellHeight);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(FONT_SIZE);
  doc.text('O.S NUMBER', cx + 2, yPosition + 4);
  cx += colWidths[0];
  doc.rect(cx, yPosition, colWidths[1] + colWidths[2], cellHeight);
  doc.setFont(undefined, 'normal');
  doc.text(surroundingDetails.os_number || '', cx + 2, yPosition + 4);
  yPosition += cellHeight;
  yPosition += 6;

  // Negative Remarks
  const negativeRemarks = surveyData.surrounding_details?.negative_remarks || '';
  if (negativeRemarks) {
    checkNewPage(15);
    yPosition += 6;
    doc.setFontSize(FONT_SIZE);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    const remarksLabel = 'OTHER NEGATIVE REMARKS: ';
    const labelW = doc.getTextWidth(remarksLabel);
    doc.text(remarksLabel, margin, yPosition);
    doc.setFont(undefined, 'normal');
    const remarksLines = doc.splitTextToSize(negativeRemarks, pageWidth - 2 * margin - labelW);
    doc.text(remarksLines[0], margin + labelW, yPosition);
    remarksLines.slice(1).forEach((line) => {
      yPosition += 5.5;
      doc.text(line, margin + labelW, yPosition);
    });
    yPosition += 8;
  }

  // Rate Information Table
  checkNewPage(30);
  const rateInfo = surveyData.rate_information || {};
  addSectionHeading('RATE INFORMATION');

  const formatRate = (rateObj, unit = '/sft') => {
    const min = rateObj?.min;
    const max = rateObj?.max;
    if (!min && !max) return 'N/A';
    return `Rs. ${min || '___'}${unit} to Rs. ${max || '___'}${unit}`;
  };

  const rateTable = [
    ['TYPE', 'RATE RANGE'],
    ['LAND', formatRate(rateInfo.land_rate)],
    ['FLAT/VILLA COMPOSITE', formatRate(rateInfo.flat_villa_composite_rate)],
    ['COMMERCIAL', formatRate(rateInfo.commercial_rate)],
    ['RENT', formatRate(rateInfo.commercial_rent, '/sft/month')]
  ];
  yPosition = addTable(rateTable, margin, yPosition, [60, 130]);
  yPosition += 6;

  // Reference section
  checkNewPage(18);
  const references = surveyData.rate_information?.references || [];
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.text('REFERENCE:', margin, yPosition);
  doc.setFont(undefined, 'normal');
  yPosition += 6;
  if (references.length > 0) {
    references.forEach((ref, i) => {
      const prefix = `${i + 1}. `;
      const refText = String(ref || '');
      // Measure prefix width so wrapped lines indent correctly
      const prefixWidth = doc.getTextWidth(prefix);
      const maxWidth = pageWidth - 2 * margin;
      // Split the full line; if it fits in one line this returns a single-element array
      const lines = doc.splitTextToSize(prefix + refText, maxWidth);
      checkNewPage(lines.length * 5 + 3);
      lines.forEach((line, lineIdx) => {
        doc.text(line, margin, yPosition);
        yPosition += 5;
      });
      yPosition += 1; // small gap between references
    });
  } else {
    doc.text('No references available.', margin, yPosition);
    yPosition += 6;
  }
  yPosition += 9;
  checkNewPage(12);
  doc.setFont(undefined, 'bold');
  doc.text('SKETCH with North direction and site dimension:', margin, yPosition);
  doc.setFont(undefined, 'normal');
  yPosition += 10;

  // Photos Section
  if (surveyData.documents && surveyData.documents.length > 0) {
    checkNewPage(60);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 51, 102);
    doc.text('PHOTOS REQUIRED', margin, yPosition);
    doc.setTextColor(0, 0, 0);
    yPosition += 8;
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    const insideText = 'INSIDE- HALL, KITCHEN, ROOMS, EC BILL, ID PROOF, LIFT, METER BOARD, FLAT NO., AMENITIES(DG,LIFT,GYM,HOMETHEATRE, POOL,Etc)';
    const insideLines = doc.splitTextToSize(insideText, pageWidth - 2 * margin);
    insideLines.forEach(line => {
      doc.text(line, margin, yPosition);
      yPosition += 5.5;
    });
    yPosition += 2;
    const outsideText = 'OUT SIDE- NAME/NUMBER BOARD, ELEVATION, ROAD, SELFIE WITH CUSTOMER & PROPERTY';
    const outsideLines = doc.splitTextToSize(outsideText, pageWidth - 2 * margin);
    outsideLines.forEach(line => {
      doc.text(line, margin, yPosition);
      yPosition += 5.5;
    });
    yPosition += 2;
    const industrialText = 'INDUSTRIAL PROPERTIES- DISHANK, LAND MEASUMENT IN APP, LAT&LONG';
    const industrialLines = doc.splitTextToSize(industrialText, pageWidth - 2 * margin);
    industrialLines.forEach(line => {
      doc.text(line, margin, yPosition);
      yPosition += 5.5;
    });
    yPosition += 6;

    // Add images in 2-column grid
    const imgColWidth = (pageWidth - 2 * margin - 4) / 2;
    const imgHeight = imgColWidth * 0.75;
    let colIndex = 0;

    for (let i = 0; i < surveyData.documents.length; i++) {
      const docItem = surveyData.documents[i];
      if (!docItem.file_path) continue;

      const base64Image = docImageMap.get(docItem.file_path);
      if (!base64Image) continue;

        // Start new row if left column
        if (colIndex === 0) {
          checkNewPage(imgHeight + 14);
        }

        const imgX = margin + colIndex * (imgColWidth + 4);

        // Image title
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 51, 102);
        doc.text(docItem.title || `Photo ${i + 1}`, imgX, yPosition + 4);
        doc.setTextColor(0, 0, 0);

        // Image is already optimised (resized + JPEG 85%) — 'MEDIUM' compression
        // gives a good balance. Switch back to 'FAST' if generation feels slow.
        doc.addImage(base64Image, 'JPEG', imgX, yPosition + 6, imgColWidth, imgHeight, undefined, 'MEDIUM');

        colIndex++;
        if (colIndex === 2) {
          colIndex = 0;
          yPosition += imgHeight + 14;
        }
    }
    // If odd number of images, move yPosition
    if (colIndex === 1) {
      yPosition += imgHeight + 14;
    }
  }

  // Remarks
  checkNewPage(25);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text('Remarks (if any):', margin, yPosition);
  doc.setTextColor(0, 0, 0);
  yPosition += 8;
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  if (surveyData.comments && surveyData.comments.length > 0) {
    surveyData.comments.forEach((comment, idx) => {
      checkNewPage(15);
      if (idx > 0) {
        yPosition += 2; // Space between comments
      }
      doc.setFont(undefined, 'bold');
      doc.text(`${comment.author?.username || 'User'}:`, margin, yPosition);
      doc.setFont(undefined, 'normal');
      const commentLines = doc.splitTextToSize(comment.comment || '', pageWidth - 2 * margin - 30);
      commentLines.forEach((line, lineIdx) => {
        yPosition += 6;
        doc.text(line, margin + 5, yPosition);
      });
      yPosition += 3;
    });
  } else {
    doc.setFont(undefined, 'italic');
    doc.setTextColor(128, 128, 128);
    doc.text('No remarks available.', margin, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
  }

  // Footer on each page
  const totalPages = doc.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    // Add logo on each page (top right)
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', pageWidth - 30, margin, 20, 8);
    }

    // Footer text
    doc.setFontSize(7);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Page ${i} of ${totalPages} | Survey: ${surveyData.survey_number || 'N/A'} | Generated: ${new Date().toLocaleString()}`,
      pageWidth / 2,
      pageHeight - 5,
      { align: 'center' }
    );
    doc.setTextColor(0, 0, 0);
  }

  return doc;
};

